import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2, Target, TrendingUp, Shield, AlertTriangle, CheckCircle2,
  XCircle, Clock, Copy, Download, Landmark, PiggyBank, Percent,
  DollarSign, ArrowRight, ExternalLink, Loader2, Sparkles,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { InvestmentReadout } from "@/components/InvestmentReadout";
import { toast } from "@/hooks/use-toast";
import {
  calculateScore, buildScoringInputFromContext, type ScoringResult,
  type ScoringInput, type DerivedMetrics, type RiskFlag, type SensitivityScenario,
} from "@/lib/scoring-engine";
import type { DealExportIntelligence } from "@/lib/deal-share-summary";
import { verdictShort, verdictLong } from "@/lib/verdict-display";

// ── Verdict Config ──
const verdictConfig = {
  GO: { color: "text-green-500", bg: "bg-green-500/10", border: "border-green-500/30", label: verdictLong("GO"), icon: CheckCircle2 },
  MAYBE: { color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/30", label: verdictLong("MAYBE"), icon: AlertTriangle },
  "NO-GO": { color: "text-destructive", bg: "bg-destructive/10", border: "border-destructive/30", label: verdictLong("NO-GO"), icon: XCircle },
};

const fmt = (n: number) => n.toLocaleString("en-US");
const fmtDollar = (n: number) => `$${fmt(Math.round(n))}`;
const formatValidation = (status: string | null | undefined) => status ? status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()) : "Unscored";

// ── Section Wrapper ──
const Section = ({ title, icon: Icon, children, delay = 0 }: {
  title: string; icon: React.ElementType; children: React.ReactNode; delay?: number;
}) => (
  <motion.section
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.4 }}
    className="space-y-4"
  >
    <div className="flex items-center gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
        <Icon className="w-4 h-4 text-accent" />
      </div>
      <h2 className="font-display text-lg font-semibold text-foreground tracking-tight">{title}</h2>
    </div>
    {children}
  </motion.section>
);

// ── Metric Card ──
const MetricCard = ({ label, value, icon: Icon }: { label: string; value: string; icon: React.ElementType }) => (
  <div className="p-4 rounded-xl border border-border bg-card space-y-1">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
      <span className="micro-label text-muted-foreground">{label}</span>
    </div>
    <p className="font-display text-2xl font-bold text-foreground">{value}</p>
  </div>
);

const DealSummaryPublic = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [deal, setDeal] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);

  // Fetch deal by share_slug
  useEffect(() => {
    const fetchDeal = async () => {
      if (!slug) { setError("No deal specified."); setLoading(false); return; }

      // Use narrowed RPC: returns ONLY curated share_payload + display fields,
      // never raw underwriting inputs / final_result.
      const { data, error: fetchErr } = await supabase
        .rpc("get_shared_deal", { _share_slug: slug })
        .maybeSingle();

      if (fetchErr || !data) {
        setError("Deal not found or is no longer shared.");
        setLoading(false);
        return;
      }

      setDeal(data);
      setLoading(false);
    };
    fetchDeal();
  }, [slug]);

  // Compute scoring from share_payload
  const sharePayload = deal?.share_payload as any;
  const result: ScoringResult | null = useMemo(() => {
    if (!sharePayload) return null;
    return sharePayload.result || null;
  }, [sharePayload]);

  const ctx = sharePayload?.context || {};
  const m: DerivedMetrics | null = result?.derived || null;
  const documentIntelligence = sharePayload?.document_intelligence as DealExportIntelligence | undefined;

  // Dynamic OG meta tags + page title
  useEffect(() => {
    if (!result || !m) return;

    const units = ctx.unit_count || "?";
    const market = (ctx.market_type || "").charAt(0).toUpperCase() + (ctx.market_type || "").slice(1);
    const verdictLabel = verdictLong(result.verdict);
    const title = `${units}-Unit ${market} Deal — Score ${result.overall_score}/100 (${verdictLabel}) | Brickshare`;
    const description = `Multifamily ${(ctx.exit_strategy || "value-add").replace(/_/g, " ")} opportunity. Deal Score: ${result.overall_score}/100. IRR: ${m.proxy_irr > 0 ? m.proxy_irr.toFixed(1) + "%" : "N/A"}, DSCR: ${m.dscr > 0 ? m.dscr.toFixed(2) + "x" : "N/A"}, Cap Rate: ${m.cap_rate_going_in > 0 ? m.cap_rate_going_in.toFixed(2) + "%" : "N/A"}. Analyzed with Brickshare.`;
    const url = window.location.href;

    document.title = title;

    const setMeta = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setMetaName = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Open Graph
    setMeta("og:type", "website");
    setMeta("og:title", title);
    setMeta("og:description", description);
    setMeta("og:url", url);
    setMeta("og:site_name", "Brickshare");

    // Twitter Card
    setMetaName("twitter:card", "summary");
    setMetaName("twitter:title", title);
    setMetaName("twitter:description", description);

    // General meta description
    setMetaName("description", description);

    return () => {
      document.title = "Brickshare";
      ["og:type", "og:title", "og:description", "og:url", "og:site_name"].forEach(p => {
        document.querySelector(`meta[property="${p}"]`)?.remove();
      });
      ["twitter:card", "twitter:title", "twitter:description", "description"].forEach(n => {
        document.querySelector(`meta[name="${n}"]`)?.remove();
      });
    };
  }, [result, m, ctx]);

  // Load AI summary
  useEffect(() => {
    if (!sharePayload || aiSummary) return;
    // Check if pre-generated
    if (sharePayload.ai_summary) {
      setAiSummary(sharePayload.ai_summary);
      return;
    }
    // Generate on-the-fly
    const generate = async () => {
      setAiLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("deal-package-summary", {
          body: { share_payload: sharePayload },
        });
        if (data?.summary) {
          setAiSummary(data.summary);
        }
      } catch {
        // Fail silently — summary is optional
      }
      setAiLoading(false);
    };
    generate();
  }, [sharePayload, aiSummary]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast({ title: "Link copied", description: "Share this deal summary with investors." });
  };

  const handleExportPDF = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error || !result || !m) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <XCircle className="w-12 h-12 text-muted-foreground" />
        <h1 className="font-display text-xl font-semibold text-foreground">{error || "Deal data unavailable"}</h1>
        <Button variant="outline" onClick={() => navigate("/")}>Return to Brickshare</Button>
      </div>
    );
  }

  const vc = verdictConfig[result.verdict];
  const VerdictIcon = vc.icon;
  const units = ctx.unit_count || "—";
  const dealName = `${units}-Unit ${(ctx.market_type || "").charAt(0).toUpperCase() + (ctx.market_type || "").slice(1)} Deal`;

  // Section visibility — default all visible if not specified
  const vs = sharePayload?.visible_sections as Record<string, boolean> | null;
  const show = (key: string) => !vs || vs[key] !== false;

  return (
    <div className="min-h-screen bg-background">
      {/* Print-friendly styles */}
      <style>{`@media print { .no-print { display: none !important; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }`}</style>

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-10">

        {/* ── SECTION 1: DEAL HEADER ── */}
        <motion.header
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          <div className="flex items-center justify-between no-print">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
                <span className="text-accent-foreground font-display font-bold text-sm">B</span>
              </div>
              <span className="font-display font-semibold text-foreground">Brickshare<span className="text-accent">.ai</span></span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1.5 font-mono text-xs" onClick={handleCopyLink}>
                <Copy className="w-3.5 h-3.5" /> Copy Deal Link
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5 font-mono text-xs" onClick={handleExportPDF}>
                <Download className="w-3.5 h-3.5" /> Export Investor Summary
              </Button>
            </div>
          </div>

          {/* Verdict Banner */}
          <div className={`relative overflow-hidden rounded-2xl border-2 ${vc.border} ${vc.bg} p-8`}>
            <div className="flex items-start justify-between">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <VerdictIcon className={`w-8 h-8 ${vc.color}`} />
                  <h1 className={`font-display text-3xl font-bold ${vc.color}`}>{vc.label}</h1>
                </div>
                <p className="text-muted-foreground text-sm max-w-md">{result.verdict_note}</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="px-2.5 py-1 rounded-full bg-card border border-border text-xs font-mono">{ctx.asset_type || "Multifamily"}</span>
                  <span className="px-2.5 py-1 rounded-full bg-card border border-border text-xs font-mono">{units} Units</span>
                  <span className="px-2.5 py-1 rounded-full bg-card border border-border text-xs font-mono">{(ctx.exit_strategy || "value_add").replace(/_/g, " ")}</span>
                  {ctx.hold_period && <span className="px-2.5 py-1 rounded-full bg-card border border-border text-xs font-mono">{ctx.hold_period} yr hold</span>}
                </div>
              </div>
              <div className="text-right space-y-1">
                <div className={`font-display text-5xl font-bold ${vc.color}`}>{result.overall_score}</div>
                <div className="text-muted-foreground text-xs font-mono">/ {result.overall_max}</div>
                {result.confidence && (
                  <div className="text-xs font-mono text-muted-foreground mt-2">
                    Confidence: <span className="text-foreground font-semibold">{result.confidence.level}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.header>

        {/* ── SECTION 2: DEAL OVERVIEW ── */}
        {show("deal_overview") && (
          <Section title="Deal Overview" icon={Building2} delay={0.08}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard label="Asset Type" value={ctx.asset_type || "Multifamily"} icon={Building2} />
              <MetricCard label="Market" value={ctx.market_type || "—"} icon={Landmark} />
              <MetricCard label="Hold Period" value={ctx.hold_period ? `${ctx.hold_period} yrs` : "—"} icon={Clock} />
              <MetricCard label="Confidence" value={result.confidence ? `${result.confidence.level} (${result.confidence.score}/100)` : "—"} icon={Shield} />
            </div>
          </Section>
        )}

        {/* ── SECTION 3: KEY METRICS ── */}
        {show("key_metrics") && (
          <Section title="Key Metrics" icon={TrendingUp} delay={0.1}>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              <MetricCard label="IRR (est.)" value={m.proxy_irr > 0 ? `${m.proxy_irr.toFixed(1)}%` : "—"} icon={TrendingUp} />
              <MetricCard label="Cash-on-Cash" value={m.cash_on_cash !== 0 ? `${m.cash_on_cash.toFixed(1)}%` : "—"} icon={PiggyBank} />
              <MetricCard label="DSCR" value={m.dscr > 0 ? `${m.dscr.toFixed(2)}x` : "—"} icon={Landmark} />
              <MetricCard label="Cap Rate" value={m.cap_rate_going_in > 0 ? `${m.cap_rate_going_in.toFixed(2)}%` : "—"} icon={Percent} />
              <MetricCard label="Equity Created" value={m.equity_created > 0 ? fmtDollar(m.equity_created) : "—"} icon={DollarSign} />
            </div>
          </Section>
        )}

        {/* ── SECTION 4: RISK SUMMARY ── */}
        {show("risk_summary") && (
          <Section title="Risk Summary" icon={Shield} delay={0.12}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-4 rounded-xl border border-border bg-card">
                <p className="micro-label text-muted-foreground">Overall Risk</p>
                <p className="mt-2 font-display text-2xl font-bold text-foreground">{result.risk_level}</p>
                <p className="mt-1 text-xs text-muted-foreground">Verdict confidence and downside posture for external review.</p>
              </div>
              <div className="p-4 rounded-xl border border-border bg-card">
                <p className="micro-label text-muted-foreground">Document Trust</p>
                <p className="mt-2 font-display text-2xl font-bold text-foreground">
                  {documentIntelligence?.confidence_summary.label || result.confidence.level}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {documentIntelligence
                    ? `${documentIntelligence.confidence_summary.average_score}/100 across ${documentIntelligence.confidence_summary.document_count} documents`
                    : `${result.confidence.score}/100 screening confidence`}
                </p>
              </div>
              <div className="p-4 rounded-xl border border-border bg-card">
                <p className="micro-label text-muted-foreground">Validation Mix</p>
                <p className="mt-2 text-sm text-foreground">
                  {documentIntelligence
                    ? `${documentIntelligence.validation_breakdown.strong} strong • ${documentIntelligence.validation_breakdown.partial} partial • ${documentIntelligence.validation_breakdown.weak + documentIntelligence.validation_breakdown.failed} weak`
                    : "Validation detail unavailable"}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {documentIntelligence?.confidence_summary.low_confidence_documents
                    ? `${documentIntelligence.confidence_summary.low_confidence_documents} document(s) need stronger corroboration`
                    : "Current file set supports the exported conclusion"}
                </p>
              </div>
            </div>
          </Section>
        )}

        {documentIntelligence?.confidence_summary.document_count ? (
          <Section title="Investment Readout" icon={Target} delay={0.13}>
            <InvestmentReadout intelligence={documentIntelligence} compact />
          </Section>
        ) : null}

        {/* ── SECTION 5: TOP RISKS ── */}
        {show("top_risks") && (documentIntelligence?.top_risks?.length || result.flags?.length > 0) && (
          <Section title="Top Risks" icon={AlertTriangle} delay={0.14}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {(documentIntelligence?.top_risks?.length ? documentIntelligence.top_risks : result.flags.map((flag) => ({ title: flag.text, detail: flag.category }))).slice(0, 4).map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                  <div>
                    <span className="text-sm text-foreground">{item.title}</span>
                    {(item.detail || ("source" in item ? item.source : undefined)) && (
                      <p className="mt-1 text-2xs font-mono uppercase tracking-wider text-muted-foreground">
                        {[item.detail, "source" in item ? item.source : undefined].filter(Boolean).join(" • ")}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── SECTION 6: UNDERWRITING ADJUSTMENTS ── */}
        {show("underwriting_adjustments") && documentIntelligence?.underwriting_adjustments?.length > 0 && (
          <Section title="Underwriting Adjustments" icon={DollarSign} delay={0.16}>
            <div className="space-y-2">
              {documentIntelligence.underwriting_adjustments.slice(0, 4).map((item, i) => (
                <div key={i} className="p-4 rounded-xl border border-border bg-card">
                  <p className="text-sm text-foreground">{item.title}</p>
                  {(item.detail || item.source) && (
                    <p className="mt-1 text-xs text-muted-foreground">{[item.detail, item.source].filter(Boolean).join(" • ")}</p>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── SECTION 7: SUPPORTING EVIDENCE ── */}
        {show("supporting_evidence") && documentIntelligence?.documents?.length > 0 && (
          <Section title="Supporting Evidence" icon={Sparkles} delay={0.18}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {documentIntelligence.documents.slice(0, 4).map((document) => (
                <div key={document.document_id} className="p-4 rounded-xl border border-border bg-card space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-display text-sm font-semibold text-foreground">{document.title}</p>
                    <span className="text-2xs font-mono uppercase tracking-wider text-muted-foreground">
                      {document.confidence_label} {document.confidence_score}/100
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {document.lane}{document.subtype ? ` • ${document.subtype}` : ""} • {formatValidation(document.validation_status)} validation
                  </p>
                  {document.top_risk && <p className="text-sm text-foreground">Risk: {document.top_risk.title}</p>}
                  {document.top_adjustment && <p className="text-sm text-foreground">Adjustment: {document.top_adjustment.title}</p>}
                </div>
              ))}
            </div>

            {documentIntelligence.evidence_highlights.length > 0 && (
              <div className="space-y-2">
                {documentIntelligence.evidence_highlights.slice(0, 4).map((item, index) => (
                  <div key={`${item.title}-${index}`} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card">
                    <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-sm text-foreground">{item.title}</span>
                      {(item.detail || item.source) && (
                        <p className="mt-1 text-2xs font-mono uppercase tracking-wider text-muted-foreground">{[item.detail, item.source].filter(Boolean).join(" • ")}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* ── SECTION 8: OPPORTUNITY SUMMARY (AI) ── */}
        {show("opportunity_summary") && (
          <Section title="Opportunity Summary" icon={Sparkles} delay={0.15}>
            <div className="p-5 rounded-xl border border-border bg-card">
              {aiLoading ? (
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-mono">Generating summary…</span>
                </div>
              ) : aiSummary ? (
                <p className="text-sm text-foreground leading-relaxed">{aiSummary}</p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  This {ctx.asset_type || "multifamily"} {(ctx.exit_strategy || "value-add").replace(/_/g, " ")} opportunity
                  scores {result.overall_score}/{result.overall_max} with {result.confidence?.level?.toLowerCase() || "moderate"} confidence,
                  driven by {result.drivers?.positives?.length || 0} positive factors.
                </p>
              )}
            </div>
          </Section>
        )}

        {/* ── SECTION 9: OPPORTUNITY DRIVERS ── */}
        {result.drivers?.positives?.length > 0 && (
          <Section title="Opportunity Drivers" icon={CheckCircle2} delay={0.2}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {result.drivers.positives.map((d, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-foreground">{d}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── SECTION 10: RISK CONSIDERATIONS ── */}
        {result.flags?.length > 0 && (
          <Section title="Risk Considerations" icon={AlertTriangle} delay={0.25}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {result.flags.map((f: RiskFlag, i: number) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card">
                  <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${f.severity === "major" ? "text-destructive" : "text-amber-500"}`} />
                  <div>
                    <span className="text-sm text-foreground">{f.text}</span>
                    <span className="ml-2 text-2xs font-mono uppercase text-muted-foreground">{f.category}</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── SECTION 11: SENSITIVITY SNAPSHOT ── */}
        {show("sensitivity_snapshot") && result.sensitivity?.length > 0 && (
          <Section title="Sensitivity Snapshot" icon={Target} delay={0.3}>
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 text-left micro-label text-muted-foreground">Scenario</th>
                    <th className="px-4 py-3 text-right micro-label text-muted-foreground">Est. IRR</th>
                    <th className="px-4 py-3 text-right micro-label text-muted-foreground">Deal Score</th>
                    <th className="px-4 py-3 text-right micro-label text-muted-foreground">Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {result.sensitivity.map((s: SensitivityScenario, i: number) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="px-4 py-3 font-medium text-foreground">{s.label}</td>
                      <td className="px-4 py-3 text-right font-mono text-foreground">{s.irr != null ? `${s.irr.toFixed(1)}%` : "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-foreground">{s.score}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          s.verdictShift === "GO" ? "bg-green-500/15 text-green-500"
                          : s.verdictShift === "NO-GO" ? "bg-destructive/15 text-destructive"
                          : "bg-amber-500/15 text-amber-500"
                        }`}>
                          {verdictShort(s.verdictShift)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* ── SECTION 12: NEGOTIATION INSIGHTS ── */}
        {sharePayload?.negotiation_insights && (
          <Section title="Negotiation Insights" icon={DollarSign} delay={0.35}>
            <div className="p-5 rounded-xl border border-border bg-card space-y-3">
              <h3 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wider">Recommended Deal Terms</h3>
              <div className="space-y-2">
                {(sharePayload.negotiation_insights as string[]).map((insight: string, i: number) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                    <span className="text-sm text-foreground">{insight}</span>
                  </div>
                ))}
              </div>
            </div>
          </Section>
        )}

        {/* ── SECTION 13: RECOMMENDED NEXT STEPS ── */}
        {show("next_steps") && result.actions?.length > 0 && (
          <Section title="Recommended Next Steps" icon={Shield} delay={0.4}>
            <div className="space-y-2">
              {result.actions.map((a, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                    a.priority === "high" ? "border-destructive" : a.priority === "medium" ? "border-amber-500" : "border-muted-foreground"
                  }`}>
                    <span className="text-[8px] font-mono font-bold text-muted-foreground">{i + 1}</span>
                  </div>
                  <span className="text-sm text-foreground">{a.text}</span>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* ── SECTION 9: CALL TO ACTION ── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="rounded-2xl border border-border bg-card p-8 text-center space-y-4 no-print"
        >
          <h2 className="font-display text-xl font-semibold text-foreground">Interested in this deal?</h2>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Run your own scenario with different assumptions, or analyze a new deal on Brickshare.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button
              className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
              onClick={() => navigate("/auth")}
            >
              <ArrowRight className="w-4 h-4" /> Run Your Own Scenario
            </Button>
            <Button variant="outline" className="gap-2" onClick={() => navigate("/auth")}>
              <ExternalLink className="w-4 h-4" /> Analyze Another Deal
            </Button>
          </div>
        </motion.div>

        {/* ── FOOTER ── */}
        <footer className="text-center py-6 border-t border-border">
          <span className="text-2xs font-mono text-muted-foreground/50 uppercase tracking-widest">
            Analyzed with Brickshare.ai
          </span>
        </footer>
      </div>
    </div>
  );
};

export default DealSummaryPublic;
